/**
 * Shared admin motion tokens — MUI sx / CSS only (no animation libraries).
 * Durations 160–280ms, expo ease-out, transform + opacity preferred.
 */

export const ADMIN_MOTION_EASE_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
export const ADMIN_MOTION_EASE_OUT_QUINT = "cubic-bezier(0.22, 1, 0.36, 1)";

export const ADMIN_MOTION_DURATION = {
  fast: 160,
  normal: 220,
  slow: 280,
};

const MOTION_OK = "@media (prefers-reduced-motion: no-preference)";
const MOTION_REDUCE = "@media (prefers-reduced-motion: reduce)";

export const adminKeyframes = {
  "@keyframes adminFadeUp": {
    from: { opacity: 0, transform: "translate3d(0, 8px, 0)" },
    to: { opacity: 1, transform: "translate3d(0, 0, 0)" },
  },
  "@keyframes adminFadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  "@keyframes adminToastIn": {
    from: { opacity: 0, transform: "translate3d(0, -6px, 0)" },
    to: { opacity: 1, transform: "translate3d(0, 0, 0)" },
  },
};

/** Page/card entrance — optional stagger via delay (ms). */
export function adminEntranceSx({ delay = 0, duration = ADMIN_MOTION_DURATION.normal } = {}) {
  return {
    ...adminKeyframes,
    [MOTION_OK]: {
      animation: `adminFadeUp ${duration}ms ${ADMIN_MOTION_EASE_OUT_EXPO} ${delay}ms both`,
    },
    [MOTION_REDUCE]: {
      animation: "none",
    },
  };
}

/** Overlay, alert, toast — opacity-only fade. */
export function adminFadeInSx({ delay = 0, duration = ADMIN_MOTION_DURATION.fast } = {}) {
  return {
    ...adminKeyframes,
    [MOTION_OK]: {
      animation: `adminFadeIn ${duration}ms ${ADMIN_MOTION_EASE_OUT_EXPO} ${delay}ms both`,
    },
    [MOTION_REDUCE]: {
      animation: "none",
    },
  };
}

/** Stagger helper: cap delay so long lists do not cascade too slowly. */
export function adminStaggerDelay(index, step = 40, max = 200) {
  return Math.min(index * step, max);
}

/** Primary/secondary admin CTA press and hover feedback. */
export const adminButtonInteractionSx = {
  transition: [
    `transform ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `opacity ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `background-color ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `border-color ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `color ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
  ].join(", "),
  [MOTION_OK]: {
    "&:hover": {
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0) scale(0.98)",
      transitionDuration: "100ms",
    },
  },
  [MOTION_REDUCE]: {
    "&:hover, &:active": {
      transform: "none",
    },
  },
};

/** Vertical nav item hover/active polish. */
export const adminNavLinkMotionSx = {
  transition: [
    `background-color ${ADMIN_MOTION_DURATION.normal}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `color ${ADMIN_MOTION_DURATION.normal}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
    `transform ${ADMIN_MOTION_DURATION.fast}ms ${ADMIN_MOTION_EASE_OUT_EXPO}`,
  ].join(", "),
  [MOTION_OK]: {
    "&:hover:not(.active)": {
      transform: "translateX(2px)",
    },
  },
  [MOTION_REDUCE]: {
    "&:hover": {
      transform: "none",
    },
  },
};

/** MUI Alert root — theme override compatible. */
export const adminAlertEntranceSx = adminFadeInSx({
  duration: ADMIN_MOTION_DURATION.normal,
});

/** DataGrid loading / empty overlays. */
export const adminOverlayFadeSx = adminFadeInSx({
  duration: ADMIN_MOTION_DURATION.normal,
});

/** Dialog body content — runs after MUI dialog fade. */
export const adminDialogContentEntranceSx = adminEntranceSx({
  delay: 40,
  duration: ADMIN_MOTION_DURATION.fast,
});

/** react-hot-toast container. */
export const adminToastMotionSx = {
  ...adminKeyframes,
  [MOTION_OK]: {
    "& .react-hot-toast": {
      animation: `adminToastIn ${ADMIN_MOTION_DURATION.normal}ms ${ADMIN_MOTION_EASE_OUT_EXPO} both`,
    },
  },
  [MOTION_REDUCE]: {
    "& .react-hot-toast": {
      animation: "none",
    },
  },
};
